/**
 * FAQ 상세 페이지
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Badge, Button, Spinner } from 'react-bootstrap';
import AdminLayout from '../../components/layout/AdminLayout';
import { faqService } from '../../services/faqService';
import { FAQ } from '../../types/faq';

const FAQDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const faqId = id ? parseInt(id, 10) : 0;

  const { data: faq, isLoading } = useQuery({
    queryKey: ['faq', faqId],
    queryFn: () => faqService.getFAQ(faqId),
    enabled: !!faqId && faqId > 0,
  });

  const getStatusBadge = (status: string) => {
    return status === 'active' ? (
      <Badge bg="success">활성</Badge>
    ) : (
      <Badge bg="secondary">비활성</Badge>
    );
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <Spinner animation="border" variant="primary" />
        </div>
      </AdminLayout>
    );
  }

  if (!faq) {
    return (
      <AdminLayout>
        <Card>
          <Card.Body>
            <p>FAQ를 찾을 수 없습니다.</p>
            <Button variant="secondary" onClick={() => navigate('/admin/faqs')}>
              목록으로
            </Button>
          </Card.Body>
        </Card>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 style={{ color: '#fff' }}>FAQ 상세</h1>
          <div>
            <Button variant="secondary" onClick={() => navigate('/admin/faqs')} className="me-2">
              목록으로
            </Button>
            <Button variant="primary" onClick={() => navigate(`/admin/faqs/${faqId}/edit`)}>
              수정
            </Button>
          </div>
        </div>

        <Card>
          <Card.Body>
            <div className="mb-3">
              <div className="d-flex align-items-center mb-2">
                <strong className="me-2">표시 순서:</strong>
                <Badge bg="primary">{faq.display_order}</Badge>
              </div>
              {faq.category && (
                <div className="mb-2">
                  <strong>카테고리:</strong> {faq.category}
                </div>
              )}
              <div className="mb-2">
                {getStatusBadge(faq.status)}
              </div>
            </div>

            <div className="mb-3">
              <strong>등록일:</strong> {new Date(faq.created_at).toLocaleString('ko-KR')}
            </div>

            {faq.updated_at && (
              <div className="mb-3">
                <strong>수정일:</strong> {new Date(faq.updated_at).toLocaleString('ko-KR')}
              </div>
            )}

            <hr />

            <div className="mb-3">
              <h5>질문 (한국어)</h5>
              <div style={{ 
                padding: '15px', 
                backgroundColor: '#2E2E3A', 
                borderRadius: '5px',
                color: '#ccc',
                whiteSpace: 'pre-wrap'
              }}>
                {faq.question.ko || '-'}
              </div>
            </div>

            {faq.question.en && (
              <div className="mb-3">
                <h5>질문 (영어)</h5>
                <div style={{ 
                  padding: '15px', 
                  backgroundColor: '#2E2E3A', 
                  borderRadius: '5px',
                  color: '#ccc',
                  whiteSpace: 'pre-wrap'
                }}>
                  {faq.question.en}
                </div>
              </div>
            )}

            {faq.question.th && (
              <div className="mb-3">
                <h5>질문 (태국어)</h5>
                <div style={{ 
                  padding: '15px', 
                  backgroundColor: '#2E2E3A', 
                  borderRadius: '5px',
                  color: '#ccc',
                  whiteSpace: 'pre-wrap'
                }}>
                  {faq.question.th}
                </div>
              </div>
            )}

            <hr />

            <div className="mb-3">
              <h5>답변 (한국어)</h5>
              <div style={{ 
                padding: '15px', 
                backgroundColor: '#2E2E3A', 
                borderRadius: '5px',
                color: '#ccc',
                whiteSpace: 'pre-wrap'
              }}>
                {faq.answer.ko || '-'}
              </div>
            </div>

            {faq.answer.en && (
              <div className="mb-3">
                <h5>답변 (영어)</h5>
                <div style={{ 
                  padding: '15px', 
                  backgroundColor: '#2E2E3A', 
                  borderRadius: '5px',
                  color: '#ccc',
                  whiteSpace: 'pre-wrap'
                }}>
                  {faq.answer.en}
                </div>
              </div>
            )}

            {faq.answer.th && (
              <div className="mb-3">
                <h5>답변 (태국어)</h5>
                <div style={{ 
                  padding: '15px', 
                  backgroundColor: '#2E2E3A', 
                  borderRadius: '5px',
                  color: '#ccc',
                  whiteSpace: 'pre-wrap'
                }}>
                  {faq.answer.th}
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default FAQDetail;

